import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, Minimize, ZoomIn, ZoomOut } from "lucide-react";

interface FullscreenToolbarProps {
  onPrevious: () => void;
  onNext: () => void;
  onExit: () => void;
  onZoomIn: () => void;
  onZoomOut: () => void;
  canGoPrevious: boolean;
  canGoNext: boolean;
  canZoomIn: boolean;
  canZoomOut: boolean;
  accentColor: string;
}

export function FullscreenToolbar({
  onPrevious,
  onNext,
  onExit,
  onZoomIn,
  onZoomOut,
  canGoPrevious,
  canGoNext,
  canZoomIn,
  canZoomOut,
  accentColor
}: FullscreenToolbarProps) {
  return (
    <div 
      className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-0.5 px-2 py-1.5 bg-popover/90 border border-border/60 rounded-md shadow-2xl backdrop-blur-xl cursor-auto"
      data-testid="fullscreen-toolbar"
      onClick={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
    >
      <Button
        variant="ghost"
        size="sm"
        className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground hover:bg-accent/60 rounded-sm transition-colors duration-100"
        onPointerDown={(e) => {
          e.stopPropagation();
          onPrevious();
        }}
        disabled={!canGoPrevious}
        title="Previous page (Left Arrow)"
        data-testid="button-previous-page"
      >
        <ChevronLeft className="h-3.5 w-3.5" />
      </Button>

      <div className="h-4 w-px bg-border/40 mx-0.5" />

      <Button
        variant="ghost"
        size="sm"
        className="h-7 w-7 p-0 hover:bg-accent/60 rounded-sm transition-colors duration-100"
        onPointerDown={(e) => {
          e.stopPropagation();
          onExit();
        }}
        title="Exit fullscreen (ESC)"
        data-testid="button-exit-fullscreen"
        style={{ color: accentColor }}
      >
        <Minimize className="h-3.5 w-3.5" />
      </Button>

      <div className="h-4 w-px bg-border/40 mx-0.5" />

      <Button
        variant="ghost"
        size="sm"
        className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground hover:bg-accent/60 rounded-sm transition-colors duration-100"
        onPointerDown={(e) => {
          e.stopPropagation();
          onZoomOut();
        }}
        disabled={!canZoomOut}
        title="Zoom out"
        data-testid="button-fullscreen-zoom-out"
      >
        <ZoomOut className="h-3.5 w-3.5" />
      </Button>

      <Button
        variant="ghost"
        size="sm"
        className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground hover:bg-accent/60 rounded-sm transition-colors duration-100"
        onPointerDown={(e) => {
          e.stopPropagation();
          onZoomIn();
        }}
        disabled={!canZoomIn}
        title="Zoom in"
        data-testid="button-fullscreen-zoom-in"
      >
        <ZoomIn className="h-3.5 w-3.5" />
      </Button>

      <div className="h-4 w-px bg-border/40 mx-0.5" />

      <Button
        variant="ghost"
        size="sm"
        className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground hover:bg-accent/60 rounded-sm transition-colors duration-100"
        onPointerDown={(e) => {
          e.stopPropagation();
          onNext();
        }}
        disabled={!canGoNext}
        title="Next page (Right Arrow)"
        data-testid="button-next-page"
      >
        <ChevronRight className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}
